// src/components/FacilityBooking/facilityBookingApi.js
import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

// Facility Booking API uses the AWS API Gateway endpoint
const API_BASE_URL = 'https://api.innoworx.ai/Stage';

// Facility Invoice PDF API (generate → presign → upload → get) — doc base URL
// Override with REACT_APP_FACILITY_INVOICE_API_BASE_URL if different
const FACILITY_INVOICE_API_BASE_URL =
  process.env.REACT_APP_FACILITY_INVOICE_API_BASE_URL ||
  'https://x1f3lmpgxa.execute-api.ap-south-1.amazonaws.com/Stage';

/**
 * Helper function to get authorization headers
 */
const getAuthHeaders = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    const idToken = tokens?.idToken?.toString();
    return {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    };
  } catch (error) {
    console.error('Error getting auth headers:', error);
    return {
      'Content-Type': 'application/json',
    };
  }
};

/**
 * Helper function to log API errors
 */
const logApiError = (endpoint, method, error, params = {}) => {
  const errorObj = {
    status: error.status,
    statusText: error.statusText,
    endpoint,
    method,
    ...params,
  };
  
  loggingService.logError(
    'facilityBookingApiError',
    `Facility Booking API error: ${error.status || 'Network'} ${error.statusText || error.message} for ${endpoint}`,
    error,
    errorObj
  ).catch(logError => {
    console.warn('[facilityBookingApi] Failed to log error:', logError);
  });
};

/**
 * Facility Booking API Service
 */
export const facilityBookingApi = {
  /**
   * Get facility bookings with optional filters
   * @param {Object} params - Query parameters
   * @param {string} [params.siteId] - Site ID for filtering
   * @param {string} [params.facilityType] - Facility type for filtering
   * @param {string} [params.date] - Date in YYYY-MM-DD format
   * @returns {Promise<Array>} List of bookings
   */
  getBookings: async (params = {}) => {
    try {
      const headers = await getAuthHeaders();
      const queryParams = new URLSearchParams();
      
      if (params.siteId) {
        queryParams.append('site_id', params.siteId);
      }
      if (params.facilityType) {
        queryParams.append('facility_type', params.facilityType);
      }
      if (params.date) {
        queryParams.append('date', params.date);
      }

      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/facility/bookings${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const error = new Error(`HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.statusText = response.statusText;
        error.endpoint = url;
        logApiError(url, 'GET', error, params);
        throw error;
      }

      const data = await response.json();
      return data.data || data.bookings || data || [];
    } catch (error) {
      console.error('Error fetching facility bookings:', error);
      // Log network errors
      if (!error.status) {
        error.endpoint = url;
        logApiError(url, 'GET', error, params);
      }
      // Return empty array when API fails
      return [];
    }
  },

  /**
   * Get bookings by date range (legacy - use getBookings with date filter)
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise<Array>} List of bookings
   */
  getBookingsByDateRange: async (startDate, endDate) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/facility/bookings?startDate=${startDate}&endDate=${endDate}`,
        {
          method: 'GET',
          headers,
        }
      );

      if (!response.ok) {
        const error = new Error(`HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.statusText = response.statusText;
        error.endpoint = `${API_BASE_URL}/facility/bookings`;
        logApiError(`${API_BASE_URL}/facility/bookings`, 'GET', error, { startDate, endDate });
        throw error;
      }

      const data = await response.json();
      return data.data || data.bookings || data || [];
    } catch (error) {
      console.error('Error fetching bookings by date range:', error);
      if (!error.status) {
        error.endpoint = `${API_BASE_URL}/facility/bookings`;
        logApiError(`${API_BASE_URL}/facility/bookings`, 'GET', error, { startDate, endDate });
      }
      return [];
    }
  },

  /**
   * Get booking by ID
   * @param {string} bookingId - Booking ID
   * @returns {Promise<Object>} Booking details
   */
  getBookingById: async bookingId => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/facility/bookings/${bookingId}`,
        {
          method: 'GET',
          headers,
        }
      );

      if (!response.ok) {
        const error = new Error(`HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.statusText = response.statusText;
        error.endpoint = `${API_BASE_URL}/facility/bookings/${bookingId}`;
        logApiError(`${API_BASE_URL}/facility/bookings/${bookingId}`, 'GET', error, { bookingId });
        throw error;
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching booking by ID:', error);
      if (!error.status) {
        error.endpoint = `${API_BASE_URL}/facility/bookings/${bookingId}`;
        logApiError(`${API_BASE_URL}/facility/bookings/${bookingId}`, 'GET', error, { bookingId });
      }
      throw error;
    }
  },

  /**
   * Create a new facility booking
   * @param {Object} bookingData - Booking data
   * @returns {Promise<Object>} Created booking
   */
  createBooking: async bookingData => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/facility/bookings`, {
        method: 'POST',
        headers,
        body: JSON.stringify(bookingData),
      });

      if (!response.ok) {
        const error = new Error(`HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.statusText = response.statusText;
        error.endpoint = `${API_BASE_URL}/facility/bookings`;
        logApiError(`${API_BASE_URL}/facility/bookings`, 'POST', error, { bookingData });
        throw error;
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating facility booking:', error);
      if (!error.status) {
        error.endpoint = `${API_BASE_URL}/facility/bookings`;
        logApiError(`${API_BASE_URL}/facility/bookings`, 'POST', error, { bookingData });
      }
      throw error;
    }
  },

  /**
   * Update an existing facility booking
   * @param {string} bookingId - Booking ID
   * @param {Object} bookingData - Updated booking data
   * @returns {Promise<Object>} Updated booking
   */
  updateBooking: async (bookingId, bookingData) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/facility/bookings/${bookingId}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify(bookingData),
        }
      );

      if (!response.ok) {
        const error = new Error(`HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.statusText = response.statusText;
        error.endpoint = `${API_BASE_URL}/facility/bookings/${bookingId}`;
        logApiError(`${API_BASE_URL}/facility/bookings/${bookingId}`, 'PUT', error, { bookingId, bookingData });
        throw error;
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating facility booking:', error);
      if (!error.status) {
        error.endpoint = `${API_BASE_URL}/facility/bookings/${bookingId}`;
        logApiError(`${API_BASE_URL}/facility/bookings/${bookingId}`, 'PUT', error, { bookingId, bookingData });
      }
      throw error;
    }
  },

  /**
   * Delete a facility booking
   * @param {string} bookingId - Booking ID
   * @returns {Promise<Object>} Deletion result
   */
  deleteBooking: async bookingId => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/facility/bookings/${bookingId}`,
        {
          method: 'DELETE',
          headers,
        }
      );

      if (!response.ok) {
        const error = new Error(`HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.statusText = response.statusText;
        error.endpoint = `${API_BASE_URL}/facility/bookings/${bookingId}`;
        logApiError(`${API_BASE_URL}/facility/bookings/${bookingId}`, 'DELETE', error, { bookingId });
        throw error;
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting facility booking:', error);
      if (!error.status) {
        error.endpoint = `${API_BASE_URL}/facility/bookings/${bookingId}`;
        logApiError(`${API_BASE_URL}/facility/bookings/${bookingId}`, 'DELETE', error, { bookingId });
      }
      throw error;
    }
  },

  /**
   * Approve a facility booking
   * @param {string} bookingId - Booking ID
   * @returns {Promise<Object>} Approval result
   */
  approveBooking: async bookingId => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/facility/bookings/${bookingId}/approve`,
        {
          method: 'POST',
          headers,
        }
      );

      if (!response.ok) {
        const error = new Error(`HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.statusText = response.statusText;
        error.endpoint = `${API_BASE_URL}/facility/bookings/${bookingId}/approve`;
        logApiError(`${API_BASE_URL}/facility/bookings/${bookingId}/approve`, 'POST', error, { bookingId });
        throw error;
      }

      return await response.json();
    } catch (error) {
      console.error('Error approving facility booking:', error);
      if (!error.status) {
        error.endpoint = `${API_BASE_URL}/facility/bookings/${bookingId}/approve`;
        logApiError(`${API_BASE_URL}/facility/bookings/${bookingId}/approve`, 'POST', error, { bookingId });
      }
      throw error;
    }
  },

  /**
   * Reject a facility booking
   * @param {string} bookingId - Booking ID
   * @param {string} reason - Rejection reason
   * @returns {Promise<Object>} Rejection result
   */
  rejectBooking: async (bookingId, reason) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/facility/bookings/${bookingId}/reject`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ reason }),
        }
      );

      if (!response.ok) {
        const error = new Error(`HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.statusText = response.statusText;
        error.endpoint = `${API_BASE_URL}/facility/bookings/${bookingId}/reject`;
        logApiError(`${API_BASE_URL}/facility/bookings/${bookingId}/reject`, 'POST', error, { bookingId, reason });
        throw error;
      }

      return await response.json();
    } catch (error) {
      console.error('Error rejecting facility booking:', error);
      if (!error.status) {
        error.endpoint = `${API_BASE_URL}/facility/bookings/${bookingId}/reject`;
        logApiError(`${API_BASE_URL}/facility/bookings/${bookingId}/reject`, 'POST', error, { bookingId, reason });
      }
      throw error;
    }
  },

  /**
   * Get available time slots for a facility on a specific date
   * @param {string} facilityType - Facility type
   * @param {string} date - Date in YYYY-MM-DD format
   * @returns {Promise<Object>} Available time slots
   */
  getAvailableSlots: async (facilityType, date) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/facility/bookings/availability?facility_type=${encodeURIComponent(facilityType)}&date=${date}`,
        {
          method: 'GET',
          headers,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Error fetching available slots:', error);
      return { available_slots: [] };
    }
  },

  /**
   * Get facility invoices for calendar display
   * @param {Object} params - Query parameters
   * @param {string} [params.siteId] - Site ID for filtering
   * @param {string} [params.dateFrom] - Date filter (YYYY-MM or YYYY-MM-DD format)
   * @param {string} [params.bookingId] - Booking ID to fetch invoices for a specific booking
   * @returns {Promise<Array>} List of facility invoices
   */
  getFacilityInvoices: async (params = {}) => {
    try {
      const headers = await getAuthHeaders();
      const queryParams = new URLSearchParams();
      
      if (params.siteId) {
        queryParams.append('site_id', params.siteId);
      }
      if (params.dateFrom) {
        queryParams.append('date_from', params.dateFrom);
      }
      if (params.bookingId) {
        queryParams.append('booking_id', params.bookingId);
      }

      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/facility/invoices${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const error = new Error(`HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.statusText = response.statusText;
        error.endpoint = url;
        logApiError(url, 'GET', error, params);
        throw error;
      }

      const data = await response.json();
      return data.invoices || data || [];
    } catch (error) {
      console.error('Error fetching facility invoices:', error);
      if (!error.status) {
        error.endpoint = url;
        logApiError(url, 'GET', error, params);
      }
      return [];
    }
  },

  /**
   * Get facility invoices by booking ID
   * @param {string} bookingId - Booking ID
   * @returns {Promise<Array>} List of invoices for the booking
   */
  getInvoicesByBookingId: async (bookingId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/facility/invoices?booking_id=${encodeURIComponent(bookingId)}`,
        {
          method: 'GET',
          headers,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.invoices || data || [];
    } catch (error) {
      console.error('Error fetching invoices by booking ID:', error);
      return [];
    }
  },

  // ==================== FACILITY INVOICE PDF (generate → presign → upload → get) ====================
  //
  // API: Facility Invoice PDF. Flow: 1) generate 2) presign 3) frontend PUT to S3 4) get (optional).
  // Base URL: FACILITY_INVOICE_API_BASE_URL (doc: https://x1f3lmpgxa.execute-api.ap-south-1.amazonaws.com/Stage).
  // Auth: Cognito JWT with custom:tenant_id.

  /**
   * Generate an invoice record for a facility booking. Does not create a PDF; use this before
   * rendering the PDF and uploading via presign + uploadInvoicePdfToS3.
   * @param {string} bookingId - Booking ID
   * @returns {Promise<{ message: string, invoice: Object }>} { message, invoice } (201)
   */
  generateInvoiceRecord: async (bookingId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${FACILITY_INVOICE_API_BASE_URL}/facility/invoices/generate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ booking_id: bookingId }),
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error generating facility invoice record:', error);
      throw error;
    }
  },

  /**
   * Get a presigned PUT URL to upload the facility invoice PDF to S3. Updates the invoice
   * with the S3 key and sets bill_file_status to pending_upload.
   * @param {string} invoiceId - Invoice ID from generateInvoiceRecord
   * @param {Object} [options] - Optional overrides
   * @param {string} [options.fileName] - File name (default: facility-invoice-{invoiceId}.pdf)
   * @param {string} [options.contentType] - MIME type (default: application/pdf)
   * @returns {Promise<{ invoice_id, bucket, object_key, content_type, upload_url, expires_in }>}
   */
  presignInvoiceUpload: async (invoiceId, options = {}) => {
    try {
      const headers = await getAuthHeaders();
      const body = {};
      if (options.fileName != null) body.file_name = options.fileName;
      if (options.contentType != null) body.content_type = options.contentType;

      const response = await fetch(
        `${FACILITY_INVOICE_API_BASE_URL}/facility/invoices/${encodeURIComponent(invoiceId)}/presign`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting presigned invoice upload URL:', error);
      throw error;
    }
  },

  /**
   * Upload the rendered facility invoice PDF to S3 using the presigned PUT URL. Do not send
   * Authorization; the presigned URL includes auth. Set Content-Type to the value returned
   * from presignInvoiceUpload (typically application/pdf).
   * @param {string} uploadUrl - Presigned PUT URL from presignInvoiceUpload
   * @param {Blob|ArrayBuffer|File} pdfBlob - PDF binary (Blob, ArrayBuffer, or File)
   * @param {string} [contentType='application/pdf'] - MIME type for the Content-Type header
   * @returns {Promise<void>} Resolves on success; throws on non-2xx
   */
  uploadInvoicePdfToS3: async (uploadUrl, pdfBlob, contentType = 'application/pdf') => {
    try {
      const body = pdfBlob instanceof Blob || pdfBlob instanceof File
        ? pdfBlob
        : new Blob([pdfBlob], { type: contentType });

      const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body,
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `S3 upload failed: ${response.status}`);
      }
    } catch (error) {
      console.error('Error uploading facility invoice PDF to S3:', error);
      throw error;
    }
  },

  /**
   * Get a facility invoice by ID. Optionally include a presigned download URL for the PDF.
   * @param {string} invoiceId - Invoice ID
   * @param {Object} [options] - Query options
   * @param {boolean} [options.includeDownloadUrl] - When true, response includes download_url and download_url_expires_in
   * @returns {Promise<{ invoice: Object, download_url?: string, download_url_expires_in?: number }>}
   */
  getInvoice: async (invoiceId, options = {}) => {
    try {
      const headers = await getAuthHeaders();
      const queryParams = new URLSearchParams();
      if (options.includeDownloadUrl === true) {
        queryParams.append('include_download_url', 'true');
      }
      const queryString = queryParams.toString();
      const url = `${FACILITY_INVOICE_API_BASE_URL}/facility/invoices/${encodeURIComponent(invoiceId)}${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching facility invoice by ID:', error);
      throw error;
    }
  },

  /**
   * Run the full facility invoice PDF flow: generate record → presign → upload PDF to S3.
   * Use when the frontend has rendered a PDF and wants to store it via the facility invoice API.
   * @param {string} bookingId - Booking ID
   * @param {Blob|ArrayBuffer|File} pdfBlob - Rendered PDF (Blob, ArrayBuffer, or File)
   * @param {Object} [options] - Optional
   * @param {string} [options.fileName] - Filename for presign (default: facility-invoice-{invoiceId}.pdf)
   * @param {string} [options.contentType] - MIME type (default: application/pdf)
   * @returns {Promise<{ invoiceId: string, invoice: Object }>}
   */
  generateAndUploadInvoicePdf: async (bookingId, pdfBlob, options = {}) => {
    const gen = await facilityBookingApi.generateInvoiceRecord(bookingId);
    const invoiceId = gen?.invoice?.invoice_id;
    if (!invoiceId) {
      throw new Error('Generate invoice did not return invoice_id');
    }
    const presign = await facilityBookingApi.presignInvoiceUpload(invoiceId, {
      fileName: options.fileName,
      contentType: options.contentType,
    });
    await facilityBookingApi.uploadInvoicePdfToS3(
      presign.upload_url,
      pdfBlob,
      presign.content_type || 'application/pdf'
    );
    return { invoiceId, invoice: gen.invoice };
  },

  // ==================== AMENITIES API ====================

  /**
   * Get all amenities for a site
   * @param {string} siteId - Site ID
   * @returns {Promise<Array>} List of amenities
   */
  getAmenities: async (siteId) => {
    try {
      const headers = await getAuthHeaders();
      const url = siteId 
        ? `${API_BASE_URL}/facility/amenities?site_id=${encodeURIComponent(siteId)}`
        : `${API_BASE_URL}/facility/amenities`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const error = new Error(`HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.statusText = response.statusText;
        error.endpoint = url;
        logApiError(url, 'GET', error, { siteId });
        throw error;
      }

      const data = await response.json();
      return data.data || data.amenities || data || [];
    } catch (error) {
      console.error('Error fetching amenities:', error);
      if (!error.status) {
        error.endpoint = url;
        logApiError(url, 'GET', error, { siteId });
      }
      return [];
    }
  },

  /**
   * Get amenity by ID
   * @param {string} amenityId - Amenity ID
   * @returns {Promise<Object>} Amenity details
   */
  getAmenityById: async (amenityId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/facility/amenities/${amenityId}`,
        {
          method: 'GET',
          headers,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Error fetching amenity by ID:', error);
      throw error;
    }
  },

  /**
   * Create a new amenity
   * @param {Object} amenityData - Amenity data
   * @returns {Promise<Object>} Created amenity
   */
  createAmenity: async (amenityData) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/facility/amenities`, {
        method: 'POST',
        headers,
        body: JSON.stringify(amenityData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating amenity:', error);
      throw error;
    }
  },

  /**
   * Update an amenity
   * @param {string} amenityId - Amenity ID
   * @param {Object} amenityData - Updated amenity data
   * @returns {Promise<Object>} Updated amenity
   */
  updateAmenity: async (amenityId, amenityData) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/facility/amenities/${amenityId}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify(amenityData),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating amenity:', error);
      throw error;
    }
  },

  /**
   * Check availability for an amenity on a specific date
   * @param {string} amenityId - Amenity ID
   * @param {string} date - Date in YYYY-MM-DD format
   * @returns {Promise<Object>} Available time slots
   */
  checkAvailability: async (amenityId, date) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/facility/bookings/availability?amenity_id=${encodeURIComponent(amenityId)}&date=${date}`,
        {
          method: 'GET',
          headers,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Error checking availability:', error);
      return { available_slots: [] };
    }
  },

  /**
   * Cancel a booking
   * @param {string} bookingId - Booking ID
   * @returns {Promise<Object>} Cancellation result
   */
  cancelBooking: async (bookingId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/facility/bookings/${bookingId}/cancel`,
        {
          method: 'POST',
          headers,
        }
      );

      if (!response.ok) {
        const error = new Error(`HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.statusText = response.statusText;
        error.endpoint = `${API_BASE_URL}/facility/bookings/${bookingId}/cancel`;
        logApiError(`${API_BASE_URL}/facility/bookings/${bookingId}/cancel`, 'POST', error, { bookingId });
        throw error;
      }

      return await response.json();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      if (!error.status) {
        error.endpoint = `${API_BASE_URL}/facility/bookings/${bookingId}/cancel`;
        logApiError(`${API_BASE_URL}/facility/bookings/${bookingId}/cancel`, 'POST', error, { bookingId });
      }
      throw error;
    }
  },
};

export default facilityBookingApi;
